import { useState, useEffect, useCallback } from "react";
import {
  Lock, Plus, Trash2, Check, RefreshCw, Eye, EyeOff,
  Server, Database, Globe, CreditCard, Mail, Shield, Download, HelpCircle, MessageCircle
} from "lucide-react";
import api, { type ProjectCredential } from "../api";
import type { WorkspaceCredential, CredentialServiceStatus } from "../api";
import { useAuth } from "../context/AuthContext";

/* ═══════════════════════════════════════════════════════════════ */
/*  CREDENTIALS PAGE — BYOK Management                            */
/* ═══════════════════════════════════════════════════════════════ */

const WORKSPACE_SERVICES = [
  { id: "github", label: "GitHub", icon: Globe, desc: "Workspace secret for app environments; repository access is connected separately." },
  { id: "railway", label: "Railway", icon: Server, desc: "API token for deployment management" },
  { id: "netlify", label: "Netlify", icon: Globe, desc: "Personal access token for site deploys" },
  { id: "mailgun", label: "Mailgun", icon: Mail, desc: "API key for email sending" },
  { id: "cloudflare", label: "Cloudflare", icon: Shield, desc: "API token for DNS and CDN" },
] as const;

const PROJECT_SERVICES = [
  { id: "stripe", label: "Stripe", keys: ["secret_key", "webhook_secret"], icon: CreditCard },
  { id: "supabase", label: "Supabase", keys: ["project_url", "anon_key", "service_role_key"], icon: Database },
  { id: "mailgun_domain", label: "Mailgun Domain", keys: ["domain"], icon: Mail },
] as const;

export function CredentialsPage() {
  const { subscriber } = useAuth();
  const [tab, setTab] = useState<"workspace" | "project">("workspace");
  const [workspaceCreds, setWorkspaceCreds] = useState<WorkspaceCredential[]>([]);
  const [serviceStatus, setServiceStatus] = useState<CredentialServiceStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingService, setAddingService] = useState<string | null>(null);
  const [tokenValue, setTokenValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void loadWorkspaceData();
  }, [subscriber?.id]);

  async function loadWorkspaceData() {
    if (!subscriber?.id) return;
    setLoading(true);
    try {
      const [creds, status] = await Promise.all([
        api.getWorkspaceCredentials(subscriber.id),
        api.getCredentialStatus(subscriber.id),
      ]);
      setWorkspaceCreds(creds);
      setServiceStatus(status);
    } catch {
      // Credential service details must not be copied into browser logs.
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(service: string) {
    if (!tokenValue.trim()) return;
    setSaving(true);
    try {
      if (!subscriber?.id) return;
      await api.saveWorkspaceCredential(subscriber.id, service, tokenValue.trim());
      setTokenValue("");
      setAddingService(null);
      await loadWorkspaceData();
    } catch {
      // Keep the entered secret available for a retry without logging service errors.
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.deleteWorkspaceCredential(id);
      await loadWorkspaceData();
    } catch {
      // The existing entry remains visible if deletion fails.
    }
  }

  async function handleTest(id: string) {
    try {
      await api.testWorkspaceCredential(id);
      await loadWorkspaceData();
    } catch {
      // Keep the stored state until a successful refresh.
    }
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-5)" }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            <Lock size={20} /> Credentials
          </h2>
          <p className="muted" style={{ marginTop: 4, fontSize: 13 }}>
            Bring Your Own Keys — workspace and project-level credential management.
          </p>
        </div>
        <button className="btn btn-ghost" onClick={loadWorkspaceData} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Security Notice */}
      <div className="card" style={{
        padding: "var(--space-3) var(--space-4)",
        marginBottom: "var(--space-5)",
        borderLeft: "3px solid var(--green)",
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
      }}>
        <Shield size={16} style={{ color: "var(--green)", flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>AES-256-GCM Encrypted</div>
          <div className="muted" style={{ fontSize: 12 }}>
            All credentials are encrypted at rest. Encryption keys are stored in Railway environment variables, separate from the database.
          </div>
        </div>
      </div>

      {/* Help & Support */}
      <div className="card" style={{
        padding: "var(--space-4)",
        marginBottom: "var(--space-5)",
        borderLeft: "3px solid var(--accent)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <HelpCircle size={18} style={{ color: "var(--accent)", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Help & Support</div>
            <p className="muted" style={{ fontSize: 12, margin: 0, lineHeight: 1.5 }}>
              For pipeline issues, configuration questions, or bug reports, email{' '}
              <a href="mailto:support@ocnexus.dev" style={{ color: "var(--accent)", textDecoration: "underline" }}>support@ocnexus.dev</a>
              {' '}or use the chat widget in the bottom-right corner.
            </p>
            <p className="muted" style={{ fontSize: 11, margin: "6px 0 0", color: "var(--text-muted)" }}>
              When reporting a bug, include your pipeline ID and executor type for fastest resolution.
            </p>
          </div>
          <button
            onClick={() => (window as any).LumenIQSupport?.open()}
            className="btn btn-ghost"
            style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}
          >
            <MessageCircle size={12} /> Open Chat
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "var(--space-1)", marginBottom: "var(--space-4)", borderBottom: "1px solid var(--border)" }}>
        <button
          onClick={() => setTab("workspace")}
          style={{
            padding: "var(--space-2) var(--space-4)",
            fontSize: 13,
            fontWeight: tab === "workspace" ? 600 : 400,
            color: tab === "workspace" ? "var(--text)" : "var(--text-dim)",
            background: "none",
            border: "none",
            borderBottom: tab === "workspace" ? "2px solid var(--accent)" : "2px solid transparent",
            cursor: "pointer",
          }}
        >
          Workspace
        </button>
        <button
          onClick={() => setTab("project")}
          style={{
            padding: "var(--space-2) var(--space-4)",
            fontSize: 13,
            fontWeight: tab === "project" ? 600 : 400,
            color: tab === "project" ? "var(--text)" : "var(--text-dim)",
            background: "none",
            border: "none",
            borderBottom: tab === "project" ? "2px solid var(--accent)" : "2px solid transparent",
            cursor: "pointer",
          }}
        >
          Project
        </button>
      </div>

      {loading ? (
        <div className="empty-state">
          <div className="spinner" />
          <span className="muted">Loading credentials...</span>
        </div>
      ) : tab === "workspace" ? (
        <WorkspaceTab
          credentials={workspaceCreds}
          serviceStatus={serviceStatus}
          addingService={addingService}
          tokenValue={tokenValue}
          saving={saving}
          onSetAddingService={setAddingService}
          onSetTokenValue={setTokenValue}
          onSave={handleSave}
          onDelete={handleDelete}
          onTest={handleTest}
        />
      ) : (
        <ProjectTab />
      )}
    </div>
  );
}

/* ─── Workspace Tab ────────────────────────────────────────────── */

function WorkspaceTab({
  credentials, serviceStatus, addingService, tokenValue, saving,
  onSetAddingService, onSetTokenValue, onSave, onDelete, onTest,
}: {
  credentials: WorkspaceCredential[];
  serviceStatus: CredentialServiceStatus[];
  addingService: string | null;
  tokenValue: string;
  saving: boolean;
  onSetAddingService: (s: string | null) => void;
  onSetTokenValue: (v: string) => void;
  onSave: (service: string) => void;
  onDelete: (id: string) => void;
  onTest: (id: string) => void;
}) {
  const connectedServices = new Set(credentials.map(c => c.service));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      {WORKSPACE_SERVICES.map(svc => {
        const isConnected = connectedServices.has(svc.id);
        const cred = credentials.find(c => c.service === svc.id);
        const status = serviceStatus.find(s => s.service === svc.id);
        const isAdding = addingService === svc.id;
        const Icon = svc.icon;

        return (
          <div key={svc.id} className="card" style={{ padding: "var(--space-4)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                <Icon size={18} style={{ color: isConnected ? "var(--green)" : "var(--text-dim)" }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{svc.label}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{svc.desc}</div>
                  {svc.id === "github" && <a href="/?page=config">Connect GitHub repository access</a>}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                {isConnected ? (
                  <>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: "rgba(34,197,94,0.15)",
                      color: "var(--green)",
                    }}>
                      {svc.id === "github" ? "Workspace key stored" : "Connected"}
                    </span>
                    {cred?.tested_at && (
                      <span className="muted" style={{ fontSize: 11 }}>
                        Tested {new Date(cred.tested_at).toLocaleDateString()}
                      </span>
                    )}
                    <button className="btn btn-ghost" onClick={() => cred && onTest(cred.id)} style={{ fontSize: 11, padding: "2px 6px" }}>
                      <Check size={12} /> Test
                    </button>
                    <button className="btn btn-ghost" onClick={() => cred && onDelete(cred.id)} style={{ fontSize: 11, padding: "2px 6px", color: "var(--red)" }}>
                      <Trash2 size={12} />
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-ghost"
                    onClick={() => onSetAddingService(isAdding ? null : svc.id)}
                    style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <Plus size={12} /> {svc.id === "github" ? "Store workspace key" : "Connect"}
                  </button>
                )}
              </div>
            </div>

            {/* Token input form */}
            {isAdding && (
              <div style={{ marginTop: "var(--space-3)", display: "flex", gap: "var(--space-2)" }}>
                <input
                  type="password"
                  placeholder={`Paste your ${svc.label} token...`}
                  value={tokenValue}
                  onChange={(e) => onSetTokenValue(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "var(--space-2) var(--space-3)",
                    fontSize: 13,
                    background: "var(--bg-inset)",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    color: "var(--text)",
                    fontFamily: "monospace",
                  }}
                  onKeyDown={(e) => e.key === "Enter" && onSave(svc.id)}
                />
                <button
                  className="btn btn-primary"
                  onClick={() => onSave(svc.id)}
                  disabled={saving || !tokenValue.trim()}
                  style={{ fontSize: 12 }}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={() => { onSetAddingService(null); onSetTokenValue(""); }}
                  style={{ fontSize: 12 }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Project Tab ──────────────────────────────────────────────── */

function ProjectTab() {
  const [projects, setProjects] = useState<Array<{ project_id: string; display_name: string }>>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [creds, setCreds] = useState<ProjectCredential[]>([]);
  const [loadingCreds, setLoadingCreds] = useState(false);
  const [addingKey, setAddingKey] = useState<{ service: string; key_name: string } | null>(null);
  const [keyValue, setKeyValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  useEffect(() => {
    api.listProjects()
      .then((data: any) => {
        const list = (Array.isArray(data) ? data : []).filter((p: any) => p.project_id !== 'example-app');
        setProjects(list);
        if (list.length > 0) setSelectedProject(list[0].project_id);
      })
      .catch(() => {});
  }, []);

  const loadCreds = useCallback(async () => {
    if (!selectedProject) return;
    setLoadingCreds(true);
    try {
      const result = await api.getProjectCredentials(selectedProject);
      setCreds(result);
    } catch { setCreds([]); }
    finally { setLoadingCreds(false); }
  }, [selectedProject]);

  useEffect(() => { void loadCreds(); }, [loadCreds]);

  const handleSave = async () => {
    if (!addingKey || !keyValue.trim() || !selectedProject) return;
    setSaving(true);
    try {
      await api.saveProjectCredential(selectedProject, addingKey.service, addingKey.key_name, keyValue.trim());
      setAddingKey(null);
      setKeyValue('');
      await loadCreds();
    } catch { /* Keep service error details and submitted secrets out of browser logs. */ }
    finally { setSaving(false); }
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    try {
      await api.testProjectCredential(id);
      await loadCreds();
    } catch { /* ignore */ }
    finally { setTesting(null); }
  };

  const handleRevoke = async (id: string) => {
    try {
      await api.deleteProjectCredential(id);
      setConfirmRevoke(null);
      await loadCreds();
    } catch { /* ignore */ }
  };

  const STATUS_BADGE: Record<string, { bg: string; color: string; label: string }> = {
    valid: { bg: 'rgba(34,197,94,0.15)', color: 'var(--green)', label: 'Valid' },
    invalid: { bg: 'rgba(239,68,68,0.15)', color: 'var(--red)', label: 'Invalid' },
    pending: { bg: 'rgba(234,179,8,0.15)', color: 'var(--yellow)', label: 'Pending' },
  };

  return (
    <div>
      {/* Project selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <select
          value={selectedProject}
          onChange={e => setSelectedProject(e.target.value)}
          style={{
            padding: '6px 10px', fontSize: 13, background: 'var(--surface2)',
            border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)',
            fontFamily: 'var(--font-mono)', cursor: 'pointer',
          }}
        >
          {projects.map(p => (
            <option key={p.project_id} value={p.project_id} style={{ color: '#000', background: '#fff' }}>
              {p.display_name || p.project_id}
            </option>
          ))}
        </select>
        <button className="btn btn-ghost" onClick={loadCreds} style={{ fontSize: 11 }}>
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {loadingCreds ? (
        <div className="empty-state"><div className="spinner" /><span className="muted">Loading...</span></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {PROJECT_SERVICES.map(svc => {
            const Icon = svc.icon;
            const svcCreds = creds.filter(c => c.service === svc.id);

            return (
              <div key={svc.id} className="card" style={{ padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: svcCreds.length > 0 ? 10 : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Icon size={18} style={{ color: svcCreds.length > 0 ? 'var(--green)' : 'var(--text-dim)' }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{svc.label}</div>
                      {svcCreds.length === 0 && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          Keys: {svc.keys.map(k => <code key={k} style={{ fontSize: 11, marginRight: 4 }}>{k}</code>)}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn btn-ghost"
                    onClick={() => setAddingKey(addingKey?.service === svc.id ? null : { service: svc.id, key_name: svc.keys[0] })}
                    style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <Plus size={12} /> Add Key
                  </button>
                </div>

                {/* Configured keys */}
                {svcCreds.map(cred => {
                  const badge = STATUS_BADGE[cred.validation_status] || STATUS_BADGE.pending;
                  return (
                    <div key={cred.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                      borderTop: '1px solid var(--border)',
                    }}>
                      <code style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 100 }}>{cred.key_name}</code>
                      <code style={{ fontSize: 11, color: 'var(--text-dim)', flex: 1 }}>{cred.masked_value || '••••••'}</code>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                        background: badge.bg, color: badge.color,
                      }}>
                        {badge.label}
                      </span>
                      {cred.validated_at && (
                        <span className="muted" style={{ fontSize: 10 }}>
                          {new Date(cred.validated_at).toLocaleDateString()}
                        </span>
                      )}
                      <button
                        className="btn btn-ghost"
                        onClick={() => handleTest(cred.id)}
                        disabled={testing === cred.id}
                        style={{ fontSize: 10, padding: '2px 6px' }}
                      >
                        {testing === cred.id ? <><RefreshCw size={10} className="spin" /></> : <><Check size={10} /> Test</>}
                      </button>
                      {confirmRevoke === cred.id ? (
                        <>
                          <button className="btn btn-ghost" onClick={() => handleRevoke(cred.id)} style={{ fontSize: 10, padding: '2px 6px', color: 'var(--red)' }}>Confirm</button>
                          <button className="btn btn-ghost" onClick={() => setConfirmRevoke(null)} style={{ fontSize: 10, padding: '2px 6px' }}>Cancel</button>
                        </>
                      ) : (
                        <button className="btn btn-ghost" onClick={() => setConfirmRevoke(cred.id)} style={{ fontSize: 10, padding: '2px 6px', color: 'var(--text-dim)' }}>
                          <Trash2 size={10} />
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* Module dependencies */}
                {svcCreds.length > 0 && svcCreds[0].used_by && svcCreds[0].used_by.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                    Used by: {svcCreds[0].used_by.map(m => <code key={m} style={{ fontSize: 10, marginRight: 4 }}>@ocnexus/{m}</code>)}
                  </div>
                )}

                {/* Add key form */}
                {addingKey?.service === svc.id && (
                  <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                      <select
                        value={addingKey.key_name}
                        onChange={e => setAddingKey({ ...addingKey, key_name: e.target.value })}
                        style={{
                          padding: '4px 8px', fontSize: 12, background: 'var(--surface2)',
                          border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        {svc.keys.map(k => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="password"
                        placeholder={`Paste ${svc.label} ${addingKey.key_name}...`}
                        value={keyValue}
                        onChange={e => setKeyValue(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSave()}
                        style={{
                          flex: 1, padding: '6px 10px', fontSize: 12, background: 'var(--surface2)',
                          border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)',
                          fontFamily: 'monospace',
                        }}
                      />
                      <button className="btn btn-primary" onClick={handleSave} disabled={saving || !keyValue.trim()} style={{ fontSize: 12 }}>
                        {saving ? 'Validating...' : 'Save & Validate'}
                      </button>
                      <button className="btn btn-ghost" onClick={() => { setAddingKey(null); setKeyValue(''); }} style={{ fontSize: 12 }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="card" style={{
        padding: 16, marginTop: 16,
        borderLeft: '3px solid var(--accent)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Download size={14} />
          <span style={{ fontSize: 13, fontWeight: 500 }}>.env Generation</span>
        </div>
        <p className="muted" style={{ fontSize: 12 }}>
          Generate a .env file from workspace + project credentials. The file is streamed to your browser and never stored on the server.
        </p>
      </div>
    </div>
  );
}
